import { createContext, useContext, useEffect, useId, type ReactNode } from 'react'
import { apiErrorMessage } from '../lib/apiError'
import {
  Link,
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  type ToastIntent,
} from '@fluentui/react-components'

interface ToastOptions {
  title: string
  body?: string
  intent?: ToastIntent
  timeout?: number
  /**
   * A single follow-up the toast offers — in practice, Undo (#1245).
   *
   * The decision on that issue was undo over confirm: a prompt before a cheap,
   * reversible removal taxes every correct click to catch the rare wrong one,
   * while an undo after the fact costs nothing until it is needed. That trade
   * only works if the offer is actually reachable, so an action toast gets a
   * longer default timeout than a plain one — three seconds is enough to read
   * "Tag removed" and not enough to decide you did not mean it.
   */
  action?: {
    label: string
    onClick: () => void
  }
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} })

interface OutcomeCopy {
  title: string
  body: string
  intent: ToastIntent
  timeout: number
}

/**
 * What each value of the backend's `X-Broadcast-Outcome` header means to a
 * reader. Everything here is a demand that went live having told nobody; the
 * four differ only in why, and the why decides what the reader should do next.
 *
 * Timeouts are long because the reader has to finish reading before they can
 * decide whether to act — unlike a plain success, which they can ignore.
 */
export const OUTCOME_COPY: Record<string, OutcomeCopy> = {
  'none-matched': {
    title: 'Posted, but nobody was notified',
    body: 'No one matched the audience you chose. Post again with a wider audience, or invite talent directly.',
    intent: 'warning',
    timeout: 9000,
  },
  'fa-not-talent-scoped': {
    title: 'Posted, but nobody was notified',
    body: 'Talent cannot select the focus area on this demand, so it has no talent audience of its own. Invite talent directly, or post to all of DSAI.',
    intent: 'warning',
    timeout: 9000,
  },
  // Deliberate, so not a warning. Saying "pick an audience" here corrected a
  // choice the poster had just made on purpose — and pointed at a move they
  // could not re-run, since the change-stage dialog omits the stage the demand
  // is already in.
  declined: {
    title: 'Posted without notifying anyone',
    body: 'You chose not to notify anyone. Talent can still find this demand on the opportunities board.',
    intent: 'info',
    timeout: 5000,
  },
  'no-audience': {
    title: 'Posted without notifying anyone',
    body: 'This move did not ask who to notify, so nobody was told. Invite talent directly from the demand.',
    intent: 'warning',
    timeout: 9000,
  },
  // The dispatch itself raised. Distinct from the four above — those are "we
  // told nobody, and here is why that was correct or expected"; this one is
  // "we tried and it broke". It used to fall through to UNKNOWN_OUTCOME, which
  // reads "could not confirm who was notified" — softening a definite failure
  // into an uncertainty, which is the wrong way round.
  failed: {
    title: 'Posted, but the notifications failed',
    body: 'The demand is live. Sending the announcement raised an error, so assume nobody was told — check the timeline, and post again or invite talent directly.',
    intent: 'error',
    timeout: 12000,
  },
}

/**
 * The fallback, and the reason this is a map rather than an if-ladder.
 *
 * `fa-not-talent-scoped` shipped on the backend with no branch here, so it
 * rendered as nothing at all — a demand going live with nobody told, silently,
 * which is the exact failure the header was added to prevent. Any future
 * outcome now lands here instead of vanishing.
 */
export const UNKNOWN_OUTCOME: OutcomeCopy = {
  title: 'Posted — could not confirm who was notified',
  body: 'The server reported a result this page does not recognise. Check the timeline on the demand for what was sent.',
  intent: 'warning',
  timeout: 9000,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const toasterId = useId()
  const { dispatchToast } = useToastController(toasterId)

  const showToast = ({ title, body, intent = 'success', timeout, action }: ToastOptions) => {
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            action ? (
              <ToastTrigger>
                <Link onClick={action.onClick} data-testid="toast-action">
                  {action.label}
                </Link>
              </ToastTrigger>
            ) : undefined
          }
        >
          {title}
        </ToastTitle>
        {body ? <ToastBody>{body}</ToastBody> : null}
      </Toast>,
      { intent, timeout: timeout ?? (action ? 8000 : 3000) },
    )
  }

  // Unhandled promise rejections.
  //
  // An async handler that throws with nothing catching it produced exactly
  // nothing: no toast, no console entry the user would ever look at, and — with
  // no frontend telemetry anywhere — no record at all. The button appeared to
  // do nothing, which is indistinguishable from "the button is broken" and from
  // "I did not click it properly".
  //
  // This is a net, not a substitute for handling: anything caught here is a bug
  // in a call site, and the console line says so.
  //
  // Cancellations are filtered out. Axios aborts a request whenever a component
  // unmounts mid-flight, which is routine navigation, not a fault — surfacing
  // those would train people to ignore the toast that matters.
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as { name?: string; code?: string } | undefined
      if (reason?.name === 'AbortError' || reason?.code === 'ERR_CANCELED') return

      console.error('[unhandledrejection]', e.reason)
      dispatchToast(
        <Toast>
          <ToastTitle>Something went wrong</ToastTitle>
          <ToastBody>{apiErrorMessage(e.reason, 'An unexpected error occurred.')}</ToastBody>
        </Toast>,
        { intent: 'error', timeout: 9000 },
      )
    }
    window.addEventListener('unhandledrejection', onRejection)
    return () => window.removeEventListener('unhandledrejection', onRejection)
  }, [dispatchToast])

  // "Emails sent" feedback: the axios interceptor (lib/api) broadcasts this
  // whenever a response carried the X-Emails-Sent header, so any action that
  // queued notification emails surfaces a toast — no per-action wiring.
  useEffect(() => {
    const onEmailsSent = (e: Event) => {
      const count = (e as CustomEvent<{ count: number }>).detail?.count ?? 0
      if (count <= 0) return
      dispatchToast(
        <Toast>
          <ToastTitle>{`Emailed ${count} ${count === 1 ? 'person' : 'people'}`}</ToastTitle>
        </Toast>,
        { intent: 'info', timeout: 3500 },
      )
    }
    window.addEventListener('daccelerate:emails-sent', onEmailsSent)
    return () => window.removeEventListener('daccelerate:emails-sent', onEmailsSent)
  }, [dispatchToast])

  // Posting outcome. A demand can go live having told nobody — because the
  // audience matched no one, because the focus area has no talent audience at
  // all, because the poster declined, or because the path that moved it never
  // offered the choice. Every one of them used to look like a successful
  // broadcast.
  useEffect(() => {
    const onOutcome = (e: Event) => {
      const outcome = (e as CustomEvent<{ outcome: string }>).detail?.outcome ?? ''
      if (!outcome) return
      // The wire format is `sent:N`, or `sent:N;failed:M` when part of the
      // fan-out raised. This used to be `parseInt(outcome.slice(5), 10)`, which
      // returns 3 for "3;failed:2" — so a partial failure rendered as a SUCCESS
      // toast reading "notified 3 people", and the two who were never reached
      // vanished between the response header and the screen.
      //
      // Same defect #1063 fixed elsewhere (the backend says what happened, the
      // frontend discards it), reintroduced one PR later by #1070 — the change
      // that made the backend truthful in the first place. Parse the shape, not
      // a prefix.
      const m = /^sent:(\d+)(?:;failed:(\d+))?$/.exec(outcome)
      if (m) {
        const sent = Number(m[1])
        const failed = Number(m[2] ?? 0)
        const people = (k: number) => `${k} ${k === 1 ? 'person' : 'people'}`
        dispatchToast(
          <Toast>
            <ToastTitle>
              {failed > 0
                ? `Posted — notified ${people(sent)}, ${failed} could not be reached`
                : `Posted — notified ${people(sent)}`}
            </ToastTitle>
            {failed > 0 ? (
              <ToastBody>
                Check the timeline on the demand for who was missed, and invite them directly.
              </ToastBody>
            ) : null}
          </Toast>,
          failed > 0 ? { intent: 'warning', timeout: 9000 } : { intent: 'success', timeout: 4000 },
        )
        return
      }
      const copy = OUTCOME_COPY[outcome] ?? UNKNOWN_OUTCOME
      dispatchToast(
        <Toast>
          <ToastTitle>{copy.title}</ToastTitle>
          <ToastBody>{copy.body}</ToastBody>
        </Toast>,
        { intent: copy.intent, timeout: copy.timeout },
      )
    }
    window.addEventListener('daccelerate:broadcast-outcome', onOutcome)
    return () => window.removeEventListener('daccelerate:broadcast-outcome', onOutcome)
  }, [dispatchToast])

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toaster toasterId={toasterId} position="top-end" />
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}
